import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, Waypoints, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LEVEL_STYLES } from "@/components/hfacs/hfacsLevels";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PATH_STATUSES = ["Proposé", "À vérifier", "Chemin critique validé", "Rejeté"];
const PATH_STATUS_STYLES = {
  "Proposé": "bg-slate-100 text-slate-700 border-slate-300",
  "À vérifier": "bg-amber-500/15 text-amber-700 border-amber-500/30",
  "Chemin critique validé": "bg-emerald-500/20 text-emerald-700 border-emerald-500/40 font-semibold",
  "Rejeté": "bg-slate-100 text-slate-500 border-slate-300 line-through opacity-70",
};

export default function CriticalPathsTab({ analysisId }) {
  const [paths, setPaths] = useState([]);
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, f] = await Promise.all([
        axios.get(`${API}/hfacs/${analysisId}/critical-paths`),
        axios.get(`${API}/hfacs/${analysisId}/factors`),
      ]);
      setPaths(p.data);
      setFactors(f.data);
    } catch (e) {
      toast.error("Impossible de charger les chemins critiques.");
    } finally {
      setLoading(false);
    }
  }, [analysisId]);

  useEffect(() => { load(); }, [load]);

  const factorById = useMemo(() => {
    const map = {};
    factors.forEach((f) => { map[f.ID_FACTEUR] = f; });
    return map;
  }, [factors]);

  const detect = async () => {
    setDetecting(true);
    try {
      const res = await axios.post(`${API}/hfacs/${analysisId}/critical-paths/detect`);
      if (res.data.detected > 0) {
        toast.success(`${res.data.detected} chemin(s) causal(aux) potentiel(s) détecté(s).`);
      } else {
        toast.info("Aucun nouveau chemin détecté. Validez d'abord des liens causaux dans l'analyse causale.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la détection.");
    } finally {
      setDetecting(false);
    }
  };

  const updateStatus = async (chemin_id, statut) => {
    try {
      await axios.put(`${API}/hfacs/${analysisId}/critical-paths/${chemin_id}`, { statut });
      toast.success("Statut du chemin mis à jour.");
      load();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour du chemin.");
    }
  };

  return (
    <div>
      <div className="mb-4 px-4 py-3 rounded-sm border border-cyan-500/30 bg-cyan-500/5 text-xs text-cyan-700">
        La détection recherche automatiquement les chaînes de liens causaux <b>validés</b> menant à l'incident
        (Organisation → Supervision → Conditions → Actes). Une chaîne détectée reste "Proposée" jusqu'à validation
        explicite par l'équipe d'investigation.
      </div>
      <Button onClick={detect} disabled={detecting} data-testid="critical-paths-detect-button" className="bg-cyan-600 hover:bg-cyan-500 text-white gap-2 mb-6">
        {detecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        Détecter les chemins causaux
      </Button>

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : (
        <div className="space-y-4" data-testid="critical-paths-list">
          {paths.map((p) => (
            <div key={p.ID_CHEMIN} data-testid={`critical-path-row-${p.ID_CHEMIN}`} className="bg-card border border-border rounded-md p-4">
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Waypoints className="h-4 w-4 text-cyan-600" />
                  <h4 className="font-heading uppercase text-sm font-semibold">{p.NOM_CHEMIN}</h4>
                </div>
                <select
                  data-testid={`critical-path-status-select-${p.ID_CHEMIN}`}
                  value={p.STATUT}
                  onChange={(e) => updateStatus(p.ID_CHEMIN, e.target.value)}
                  className={`font-mono text-[11px] uppercase px-2 py-1 rounded-sm border ${PATH_STATUS_STYLES[p.STATUT] || ""}`}
                >
                  {PATH_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {(p.factor_ids || []).map((fid, idx) => {
                  const f = factorById[fid];
                  const style = f ? LEVEL_STYLES[f.NIVEAU] : null;
                  return (
                    <div key={`${p.ID_CHEMIN}-${fid}-${idx}`} className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-sm border ${style ? style.badge : "bg-secondary text-secondary-foreground border-border"}`}>
                        {f ? (f.JUSTIFICATION || f.FACTEUR) : fid}
                      </span>
                      {idx < p.factor_ids.length - 1 && <span className="text-muted-foreground text-xs">→</span>}
                    </div>
                  );
                })}
                <span className="text-muted-foreground text-xs">→</span>
                <span className="text-xs px-2 py-1 rounded-sm border border-orange-500/40 bg-orange-50 text-orange-700">Incident</span>
              </div>
              {p.VALIDE_PAR && (
                <p className="text-[11px] text-muted-foreground mt-2">Validé par {p.VALIDE_PAR} le {p.DATE_VALIDATION?.slice(0, 10)}</p>
              )}
            </div>
          ))}
          {paths.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Aucun chemin critique pour le moment. Validez des liens causaux dans l'onglet "Analyse causale", puis lancez la détection.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
